import { makeCrossSectionLabel } from '@editorUtils'
import { Vector3 } from 'three'
import { ImmutableVector3, toImmutable } from '@modugen/ui-toolkit'
import { getSlabBeamStepSizeFromAssembly } from 'src/utils/assemblies'
import { makeXDirectionPositive, makeZDirectionZPositive } from '../utils'
import {
  COLUMN_TEXT_DEPTH_DELTA,
  LABEL_POSITION_OFFSET,
  MEMBER_TEXT_DEPTH_DELTA,
} from './constants'

const stepWidthFormatter = new Intl.NumberFormat('de-DE', { maximumFractionDigits: 1 })

/**
 * Element label followed by its cross section label, e.g. `S-1 12/24`
 */
export const makeMemberLabelLine = (elementLabel: string, crossSection?: CrossSection) => {
  const crossSectionLabel = crossSection ? makeCrossSectionLabel(crossSection) : undefined

  return crossSectionLabel ? `${elementLabel} ${crossSectionLabel}` : elementLabel
}

/**
 * Assembly short name followed by its beam step width in cm, e.g. `HBV-20, 62,5`
 */
export const makeAssemblyLine = (assembly: Assembly) => {
  const stepWidthCM = getSlabBeamStepSizeFromAssembly(assembly) * 100

  return `${assembly.short_name}, ${stepWidthFormatter.format(stepWidthCM)}`
}

export const getPolygonCenter = (points: ImmutableVector3[]) =>
  points
    .reduce((sum, point) => sum.add(point), new ImmutableVector3(0, 0, 0))
    .divideScalar(points.length)

/**
 * Where a label sits and how its text plane is oriented in world space.
 */
export interface LabelPlacement {
  position: ImmutableVector3
  xDirection: Vector3
  yDirection: Vector3
}

/** Labels lying flat in the XY plane, i.e. readable in the plan view */
const flatPlacement = (position: ImmutableVector3): LabelPlacement => ({
  position,
  xDirection: new Vector3(1, 0, 0),
  yDirection: new Vector3(0, 1, 0),
})

/**
 * Beam and purlin labels sit at the member midpoint and are aligned with the
 * member axis, offset onto the cross section face.
 */
export const getMemberLabelPlacement = (
  start: ImmutableVector3,
  end: ImmutableVector3,
  csHeight: number,
): LabelPlacement => {
  const basePosition = start.add(end).divideScalar(2)

  const xDirection = makeXDirectionPositive(end.sub(start).normalize().v)
  const yDirectionTemp = new Vector3(0, 0, 1).cross(xDirection).normalize()
  const zDirection = xDirection.clone().cross(yDirectionTemp).normalize()
  // need to calculate y direction again because it might not be orthogonal to x and z in first calculation
  const yDirection = zDirection.clone().cross(xDirection).normalize()

  const position = basePosition
    .add(toImmutable(zDirection).multiplyScalar(csHeight / 2))
    .add(toImmutable(makeZDirectionZPositive(zDirection)).multiplyScalar(MEMBER_TEXT_DEPTH_DELTA))

  return { position, xDirection, yDirection }
}

/**
 * Column labels sit at the column midpoint on the front face of the cross section.
 */
export const getColumnLabelPlacement = (
  start: ImmutableVector3,
  end: ImmutableVector3,
  csWidth: number,
): LabelPlacement => {
  const basePosition = start.add(end).divideScalar(2)

  const xDirection = end.sub(start).normalize().v
  const yDirectionTemp = new Vector3(0, -1, 0).cross(xDirection).normalize()
  const zDirection = xDirection.clone().cross(yDirectionTemp).normalize()
  // need to calculate y direction again because it might not be orthogonal to x and z in first calculation
  const yDirection = zDirection.clone().cross(xDirection).normalize()

  const position = basePosition.add(
    toImmutable(zDirection).multiplyScalar(csWidth / 2 + COLUMN_TEXT_DEPTH_DELTA),
  )

  return { position, xDirection, yDirection }
}

/**
 * Wall labels sit above the top edge, centered over the wall base line.
 */
export const getWallLabelPlacement = (points: ImmutableVector3[]): LabelPlacement => {
  const bottomCenter = points[0].add(points[1]).divideScalar(2)
  const topZ = points.reduce((max, point) => Math.max(max, point.z), -Infinity)

  return flatPlacement(
    new ImmutableVector3(bottomCenter.x, bottomCenter.y, topZ + LABEL_POSITION_OFFSET),
  )
}

/**
 * Rip and lintel labels mirror the position-plan export placement
 * (`ripV2.planLabelPositionWorld` / `lintelV2.planLabelPositionWorld`): flat in
 * the XY plane, offset from the element center to a fixed, direction-agnostic
 * side. Y-cartesian elements read vertically and offset along x (rips left,
 * lintels right); all others read horizontally and offset along y (rips below,
 * lintels above). `offset` carries the side as its sign: negative for rips,
 * positive for lintels.
 */
export const getPositionLabelPlacement = (
  center: ImmutableVector3,
  isYCartesian: boolean,
  offset: number,
): LabelPlacement => {
  const offsetDirection = isYCartesian ? new Vector3(1, 0, 0) : new Vector3(0, 1, 0)

  return {
    position: center.add(toImmutable(offsetDirection).multiplyScalar(offset)),
    xDirection: isYCartesian ? new Vector3(0, 1, 0) : new Vector3(1, 0, 0),
    yDirection: isYCartesian ? new Vector3(-1, 0, 0) : new Vector3(0, 1, 0),
  }
}

/**
 * Vertical (roof) slab labels sit above the slab center.
 */
export const getVerticalSlabLabelPlacement = (
  points: ImmutableVector3[],
  zOffset: number,
): LabelPlacement =>
  flatPlacement(getPolygonCenter(points).add(new ImmutableVector3(0, 0, zOffset)))
